import { PackageUpgradeModal } from "./components/OfferGate";
import { getOfferCapabilities } from "../../common/offerCapabilities";
import React, { useEffect, useState } from "react";
import { Box, Header, Button, CheckBox, Text, Image } from "grommet";
import { Trash, Edit, Add } from "grommet-icons";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { AddProduct } from "./";
import { roundPrice } from "@common/utils";
import ProductService from "../../services/product.service";
import { presentAlertPrompt } from "@shared/Alert";
import noPhoto from "../../images/nophoto.jpg";
import { SortableList } from "@shared";

import PhotoService from "../../services/photo.service";
import { useTranslation } from "react-i18next";

export default function ProductsMenu(props) {
  const { t } = useTranslation();
  const [upgrade, setUpgrade] = useState(false);
  const { hotel, category } = props;
  const partnerManaged = Boolean(category.commerceConnection);
  const loxeManagesActivation =
    category.commerceConnection?.activationMode === "loxe";
  const [list, setList] = useState([]);
  const [productId, setProductId] = useState(0);
  const [openEdit, setOpenEdit] = useState(false);

  const onRefresh = React.useCallback(() => {
    ProductService.getProductsInCategory(category._id).then(
      (response) => {
        setList(response.data || []);
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  }, [category._id]);

  useEffect(() => {
    onRefresh();
  }, [onRefresh]);

  const setProductActive = async (product, active) => {
    if (active && !getOfferCapabilities(hotel).upsells) { setUpgrade(true); return; }
    try {
      await ProductService.updateProduct(category._id, {
        _id: product._id,
        active,
      });
      setList((current) =>
        current.map((item) =>
          item._id === product._id ? { ...item, active } : item,
        ),
      );
      presentToastSuccess(t("settingsUpsell.activationUpdated"));
    } catch (error) {
      presentToastErrorContent(error);
    }
  };

  const SortableItem = ({ value }) => (
    <SortableList.Item id={value._id}>
      <Box
        className="upsell-product-row"
        direction="row"
        align="center"
        background={!value.active ? "inactive" : "transparent"}
      >
        <Box justify="center" className="upsell-product-drag">
          <SortableList.DragHandle />
        </Box>
        <Box
          direction="row"
          gap="medium"
          align="center"
          minWidth={{ min: "0" }}
        >
          <Box className="upsell-product-image">
            <Image
              height="100%"
              width="100%"
              fit="cover"
              src={PhotoService.getURL(value.photo) || noPhoto}
              className="sortable-item-image"
              onClick={() => handleEdit(value._id)}
            />
          </Box>
          <Box gap="xsmall" minWidth={{ min: "0" }}>
            <Text weight="bold">{value.name}</Text>
            {category.isMenu && value.subCategory && (
              <Text size="small" color="dark-5">
                {value.subCategory}
              </Text>
            )}
            {value.description && (
              <Text color="dark-6" style={{ fontStyle: "italic" }} truncate>
                {value.description}
              </Text>
            )}
            {partnerManaged && (
              <Box gap="xxsmall">
                <Text className="upsell-provider-badge" size="xsmall">
                  {category.commerceConnection.provider
                    .charAt(0)
                    .toUpperCase() +
                    category.commerceConnection.provider.slice(1)}{" "}
                  · {t("settingsUpsell.partnerManaged")}
                </Text>
                <Text size="xsmall" color="dark-5" truncate>
                  {t("settingsUpsell.externalId")}: {value.externalId}
                </Text>
                {value.providerMetadata?.chargingMode && (
                  <Text size="xsmall" color="dark-5">
                    {t("settingsUpsell.chargingMode")}:{" "}
                    {value.providerMetadata.chargingMode}
                  </Text>
                )}
                {value.providerMetadata?.postingMode && (
                  <Text size="xsmall" color="dark-5">
                    {t("settingsUpsell.postingMode")}:{" "}
                    {value.providerMetadata.postingMode}
                  </Text>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Box gap="xxsmall" align="end" justify="center">
          <Text weight="bold">
            {partnerManaged ? `${value.priceCurrency || hotel.currency} ` : "$"}
            {roundPrice(value.price)}
          </Text>
          {partnerManaged && (
            <Text
              size="xsmall"
              color={value.providerPublished ? "status-ok" : "status-critical"}
            >
              {t(
                value.providerPublished
                  ? "settingsUpsell.available"
                  : "settingsUpsell.unavailable",
              )}
            </Text>
          )}
        </Box>
        <Box direction="row" gap="small" align="center" justify="end">
          {partnerManaged && loxeManagesActivation && (
            <CheckBox
              toggle
              checked={value.active}
              onChange={({ target: { checked } }) =>
                setProductActive(value, checked)
              }
              a11yTitle={t("settingsUpsell.toggleProduct", {
                name: value.name,
              })}
            />
          )}
          <Button
            icon={<Edit size="small" />}
            size="small"
            label={t("common.edit")}
            primary
            color="status-warning"
            onClick={() => handleEdit(value._id)}
          />
          {!partnerManaged && (
            <Button
              icon={<Trash size="small" />}
              size="small"
              label={t("common.remove")}
              alignSelf="center"
              primary
              color="status-critical"
              onClick={() => handleRemoveProduct(value._id)}
            />
          )}
        </Box>
      </Box>
    </SortableList.Item>
  );

  const onSortEnd = (newSortedList) => {
    setList(newSortedList);
    const oldList = list;
    const listIds = newSortedList.map((x) => x._id);
    ProductService.updateOrder(listIds).then(
      (response) => {
        // onRefresh();
      },
      (error) => {
        setList(oldList);
        presentToastErrorContent(error);
      },
    );
  };

  const handleEditModalClose = (refresh) => {
    if (refresh) onRefresh();

    setOpenEdit(false);
  };

  const handleRemoveProduct = (productId) => {
    presentAlertPrompt({
      title: t("settings.areYouSure"),
      message: t("settingsUpsell.deleteProductConfirm"),
      onOK: () => {
        removeProduct(productId);
      },
      buttonOKText: t("common.delete"),
      buttonOKColor: "status-critical",
    });
  };

  const removeProduct = (productId) => {
    ProductService.deleteProduct(productId).then(
      (response) => {
        onRefresh();
        presentToastSuccess(t("settingsUpsell.productRemoved"));
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleEdit = (productId) => {
    setProductId(productId);
    setOpenEdit(true);
  };

  const handleAdd = () => {
    setProductId(0);
    setOpenEdit(true);
  };

  return (
    <Box gap="medium" justify="start" margin="small">
      {upgrade && <PackageUpgradeModal capability="upsells" onClose={() => setUpgrade(false)} />}
      {!partnerManaged && (
        <Header
          align="center"
          direction="row"
          justify="end"
          gap="medium"
          fill="horizontal"
        >
          <Button
            primary
            icon={<Add size="small" />}
            size="small"
            label={t("settingsUpsell.addProduct")}
            onClick={handleAdd}
          />
        </Header>
      )}
      {openEdit && (
        <AddProduct
          productId={productId}
          category={category}
          onClose={handleEditModalClose}
          hotel={hotel}
        />
      )}
      <Box className="products-container">
        <SortableList
          items={list.map((x) => {
            return { ...x, id: x._id };
          })}
          onChange={onSortEnd}
          renderItem={(item) => <SortableItem value={item} />}
        />
      </Box>
    </Box>
  );
}

ProductsMenu.propTypes = {};
ProductsMenu.defaultProps = {};
